import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { http } from "@/lib/api";
import { Printer } from "lucide-react";

/**
 * Print-friendly recipe card. Auto-opens the browser print dialog. Uses
 * plain CSS so it renders identically without any of the app chrome.
 */
export default function RecipePrint() {
  const { slug } = useParams();
  const [recipe, setRecipe] = useState(null);

  useEffect(() => {
    http.get(`/recipes/${slug}`).then((r) => setRecipe(r.data.recipe));
  }, [slug]);

  useEffect(() => {
    if (recipe) {
      const id = setTimeout(() => window.print(), 800);
      return () => clearTimeout(id);
    }
  }, [recipe]);

  if (!recipe) return <div className="p-10">Loading recipe for print…</div>;

  const totalMin = recipe.total_time_min || (recipe.prep_time_min + recipe.cook_time_min);

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "24px", fontFamily: "Georgia, 'Times New Roman', serif", color: "#111" }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
        }
        h1, h2 { font-family: 'Georgia', serif; }
      `}</style>

      <div className="no-print" style={{ marginBottom: 16 }}>
        <button onClick={() => window.print()} style={{ background: "#5C6B52", color: "white", border: "none", padding: "8px 14px", borderRadius: 20, fontWeight: 600, cursor: "pointer" }}>
          <Printer size={12} style={{ marginRight: 6, verticalAlign: "middle" }} /> Print
        </button>
      </div>

      <div style={{ textAlign: "center", fontSize: 12, letterSpacing: 2, color: "#888", marginBottom: 8 }}>
        SOCIAL PULSE · RECIPES OF THE DAY
      </div>
      <h1 style={{ textAlign: "center", fontSize: 30, margin: "4px 0 8px", lineHeight: 1.15 }}>{recipe.title}</h1>
      <div style={{ textAlign: "center", fontStyle: "italic", color: "#555", marginBottom: 16, fontSize: 14 }}>
        {recipe.cuisine} · {recipe.country}{recipe.region ? ` · ${recipe.region}` : ""}
      </div>

      {recipe.cover_photo && (
        <img src={recipe.cover_photo} alt="" style={{ width: "100%", maxHeight: 300, objectFit: "cover", borderRadius: 6, marginBottom: 16 }} />
      )}

      {recipe.short_description && (
        <p style={{ fontStyle: "italic", marginBottom: 16, lineHeight: 1.5, color: "#333" }}>{recipe.short_description}</p>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 20, fontSize: 12, textAlign: "center" }}>
        <div><b>Prep</b><br />{recipe.prep_time_min} min</div>
        <div><b>Cook</b><br />{recipe.cook_time_min} min</div>
        <div><b>Total</b><br />{totalMin} min</div>
        <div><b>Serves</b><br />{recipe.servings}</div>
      </div>

      <h2 style={{ fontSize: 18, marginBottom: 8, borderBottom: "1px solid #ccc", paddingBottom: 4 }}>Ingredients</h2>
      <ul style={{ margin: "0 0 20px 16px", padding: 0 }}>
        {(recipe.ingredients || []).map((i, idx) => (
          <li key={idx} style={{ marginBottom: 4 }}>
            <b>{[i.quantity, i.unit].filter(Boolean).join(" ")}</b> {i.name}{i.optional ? " (optional)" : ""}
          </li>
        ))}
      </ul>

      <h2 style={{ fontSize: 18, marginBottom: 8, borderBottom: "1px solid #ccc", paddingBottom: 4 }}>Method</h2>
      <ol style={{ margin: "0 0 20px 20px", padding: 0 }}>
        {(recipe.steps || []).map((s, i) => (
          <li key={i} style={{ marginBottom: 10, lineHeight: 1.5 }}>
            {s.text}
            {s.tip && <div style={{ fontStyle: "italic", fontSize: 12, color: "#666", marginTop: 4 }}>Tip: {s.tip}</div>}
          </li>
        ))}
      </ol>

      {recipe.cooking_tips && recipe.cooking_tips.length > 0 && (
        <>
          <h2 style={{ fontSize: 16, marginBottom: 6 }}>Chef's tips</h2>
          <ul style={{ margin: "0 0 16px 16px", padding: 0 }}>
            {recipe.cooking_tips.map((t, i) => <li key={i}>{t}</li>)}
          </ul>
        </>
      )}

      <div style={{ textAlign: "center", fontSize: 11, color: "#888", marginTop: 24, borderTop: "1px solid #ccc", paddingTop: 12 }}>
        Recipe by {recipe.author_name} · socialpulse.com/recipes/{recipe.slug}
      </div>
    </div>
  );
}
